import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Card, Table, Tag, Select, Button } from 'antd';
import moment from 'moment-jalaali';
import MainLayout from '../../layouts/MainLayout';
import { financialApi } from '../../utils/financialApi';
import { extractApiUserMessage } from '../../utils/api';
import FinanceKPIs from '../../components/finance/dashboard/FinanceKPIs';
import UrgentActions from '../../components/finance/dashboard/UrgentActions';
import ProfitAndLoss from '../../components/finance/dashboard/ProfitAndLoss';
import CashflowForecast from '../../components/finance/dashboard/CashflowForecast';
import ReceivablesSummaryWidget from '../../components/finance/dashboard/ReceivablesSummaryWidget';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { toJalali } from '../../utils/dateUtils';

const { Title, Text } = Typography;

const JALALI_MONTHS = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];

const FinanceDashboard = () => {
  const now = moment();
  const currentYear = now.jYear();
  const currentMonth = now.jMonth() + 1;
  const [period, setPeriod] = useState({ year: currentYear, month: currentMonth });

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const res = await financialApi.getDashboard(period.year, period.month);
        const data = res?.data?.dashboard || res?.dashboard || res?.data || res;
        setDashboardData(data);
      } catch (error) {
        console.error('Failed to fetch finance dashboard:', extractApiUserMessage(error));
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [period.year, period.month]);

  const recentTransactions = dashboardData?.recent_transactions || [];

  // بازه میلادی ماه شمسی انتخابی برای گزارش سود و زیان
  const pnlStart = moment(`${period.year}/${period.month}/1`, 'jYYYY/jM/jD').format('YYYY-MM-DD');
  const pnlEnd = moment(`${period.year}/${period.month}/${moment.jDaysInMonth(period.year, period.month - 1)}`, 'jYYYY/jM/jD').format('YYYY-MM-DD');
  const isCurrentMonth = period.year === currentYear && period.month === currentMonth;
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i).map((y) => ({ value: y, label: String(y).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]) }));

  const transactionColumns = [
    {
      title: 'شماره مرجع',
      dataIndex: 'id',
      key: 'id',
      render: (id) => <Text className="text-gray-500">TRX-{id}</Text>,
    },
    {
      title: 'نوع',
      dataIndex: 'transaction_type',
      key: 'type',
      render: (type) => {
        const types = {
          receipt: { color: 'success', text: 'دریافت' },
          payment: { color: 'error', text: 'پرداخت' },
          refund: { color: 'warning', text: 'عودت' },
          adjustment: { color: 'default', text: 'تعدیل' },
          expense: { color: 'processing', text: 'هزینه' },
          transfer: { color: 'purple', text: 'انتقال' },
        };
        const config = types[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: 'مبلغ',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => {
        const isOut = ['payment', 'refund', 'expense'].includes(record.transaction_type);
        return (
          <span className={`font-semibold ${isOut ? 'text-red-500' : 'text-emerald-500'}`}>
            {isOut ? '-' : '+'}{formatPriceToman(amount || 0)}
          </span>
        );
      }
    },
    {
      title: 'تاریخ',
      dataIndex: 'created_at',
      key: 'date',
      render: (date) => (
        <span dir="ltr">
          {date ? (toJalali(date, 'jYYYY/jMM/jDD HH:mm') || toJalali(date) || '-') : '-'}
        </span>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="p-4 md:p-6 pb-24 max-w-[1600px] mx-auto animate-fade-in">
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <Title level={3} className="m-0 text-gray-800 dark:text-gray-100 font-bold">
              مدیریت مالی و خزانه
            </Title>
            <Text className="text-gray-500 dark:text-gray-400">
              داشبورد کنترل مالی، سود و زیان و پیش‌بینی نقدینگی
            </Text>
          </div>

          {/* فیلتر ماه (شمسی) */}
          <div className="flex flex-wrap items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
            <Text className="text-gray-500 dark:text-gray-400 text-sm">ماه:</Text>
            <Select
              value={period.month}
              onChange={(m) => setPeriod((p) => ({ ...p, month: m }))}
              options={JALALI_MONTHS.map((name, i) => ({ value: i + 1, label: name }))}
              style={{ width: 130, fontFamily: 'CustomFont' }}
            />
            <Select
              value={period.year}
              onChange={(y) => setPeriod((p) => ({ ...p, year: y }))}
              options={yearOptions}
              style={{ width: 100, fontFamily: 'CustomFont' }}
            />
            {!isCurrentMonth && (
              <Button onClick={() => setPeriod({ year: currentYear, month: currentMonth })} style={{ fontFamily: 'CustomFont' }}>
                ماه جاری
              </Button>
            )}
          </div>
        </div>

        {/* KPIs */}
        <FinanceKPIs kpis={dashboardData?.kpis} loading={loading} />

        {/* Receivables & Commercial Papers Summary */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24}>
            <ReceivablesSummaryWidget data={dashboardData?.receivables_summary} loading={loading} />
          </Col>
        </Row>

        {/* Profit and Loss - Full Width */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24}>
            <ProfitAndLoss startDate={pnlStart} endDate={pnlEnd} />
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="mb-6">
          {/* Urgent Actions */}
          <Col xs={24} lg={8}>
            <UrgentActions urgentActions={dashboardData?.urgent_actions} loading={loading} />
          </Col>

          {/* Cashflow Forecast */}
          <Col xs={24} lg={16}>
            <CashflowForecast />
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          {/* Recent Transactions */}
          <Col xs={24}>
            <Card 
              title={`تراکنش‌های ${JALALI_MONTHS[period.month - 1]} ${period.year}`.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d])} 
              loading={loading}
              bordered={false} 
              className="shadow-sm dark:bg-gray-800 rounded-2xl h-full"
              bodyStyle={{ padding: 0 }}
            >
              <Table 
                dataSource={recentTransactions} 
                columns={transactionColumns}
                rowKey="id"
                pagination={false}
                size="middle"
                className="custom-table"
                locale={{ emptyText: 'تراکنشی یافت نشد' }}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </MainLayout>
  );
};

export default FinanceDashboard;
